import React from "react";
import { Box, Button, Divider, Text, UnstyledButton } from "@mantine/core";
import { IconDownload, IconPrinter } from "@tabler/icons-react";
import { TreeExpansion } from "../../common/GroupedTreeTable";
import { formatCount } from "../../../utils/metricFormat";
import { COMPACT_BUTTON_PROPS } from "./compactButton";
import styles from "./ReportCanvas.module.css";

export interface ReportActionRowProps {
  /** Line items in the result; null before the first result. */
  lineCount: number | null;
  /** A newer run is loading: the count is dimmed (it is the previous run's). */
  loading?: boolean;
  expansion: TreeExpansion;
  onPrint: () => void;
  onExport: () => void;
  /** No result to print / export yet. */
  disabled: boolean;
}

/** "264 line items" / "1 line item". */
export const lineItemsLabel = (count: number): string =>
  `${formatCount(count)} line ${count === 1 ? "item" : "items"}`;

/**
 * Row above the table: line-item count, Collapse / Expand all (when grouped),
 * Print and Export (CSV). Export and print are available to
 * everyone who can see the report, once the result on screen is settled.
 */
export default function ReportActionRow({
  lineCount,
  loading = false,
  expansion,
  onPrint,
  onExport,
  disabled,
}: ReportActionRowProps) {
  return (
    <Box className={styles.actionRow}>
      <Text className={styles.count} data-loading={loading || undefined}>
        {lineCount === null ? "" : lineItemsLabel(lineCount)}
      </Text>
      <Box className={styles.spacer} />
      {expansion.hasGroups && (
        <>
          <UnstyledButton
            type="button"
            className={styles.linkButton}
            onClick={
              expansion.allCollapsed
                ? expansion.expandAll
                : expansion.collapseAll
            }
          >
            {expansion.allCollapsed ? "Expand all" : "Collapse all"}
          </UnstyledButton>
          <Divider orientation="vertical" />
        </>
      )}
      <Button
        variant="default"
        {...COMPACT_BUTTON_PROPS}
        leftSection={<IconPrinter size={14} aria-hidden />}
        onClick={onPrint}
        disabled={disabled}
      >
        Print
      </Button>
      <Button
        variant="default"
        {...COMPACT_BUTTON_PROPS}
        leftSection={<IconDownload size={14} aria-hidden />}
        onClick={onExport}
        disabled={disabled}
        title="Export as CSV"
      >
        Export
      </Button>
    </Box>
  );
}
